import Vue from "vue"
import customerHubPreferences from "./customerHubPreferences"
import customerHubApi from "@/api/customerHubApi"
import healthAdapter from "./customerHubHealthAdapter"
import { buildHubActivities, hubDay, hubStatistics } from "./customerHubActivityData"
import { store } from "@/store/stores"

const storageKey = owner => `roody:customer-hub:activity-decisions:${owner}`

function storedDecisions(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  return Object.fromEntries(Object.entries(value).filter(([id, decision]) => id && decision && typeof decision === "object" &&
    ((decision.action === "merge" && typeof decision.targetId === "string" && decision.targetId && decision.targetId !== id) ||
      (decision.action === "separate" && Array.isArray(decision.targetIds) && decision.targetIds.every(target => typeof target === "string" && target))))
    .map(([id, decision]) => [id, decision.action === "merge" ? { action: "merge", targetId: decision.targetId } : { action: "separate", targetIds: [...new Set(decision.targetIds)] }]))
}

class CustomerHubActivityService {
  constructor() {
    this.state = Vue.observable({ owner: "", busy: false, error: "", decisionError: "", activities: [], checkins: [], coverage: null, decisions: {}, requestKey: "" })
    this.version = 0
    this.token = ""
    this.pending = null
    this.healthScope = ""
  }

  start() {
    if (this.unwatch) return
    this.unwatch = store.watch(() => `${customerHubPreferences.isEnabled()}:${customerHubPreferences.state.userId}:${customerHubPreferences.state.revision}`, () => {
      const identity = customerHubPreferences.identity()
      if (!customerHubPreferences.isEnabled() || identity.userId !== this.state.owner || identity.token !== this.token) this.reset()
    })
    this.unwatchHealth = store.watch(() => healthAdapter.scope(), () => {
      const { userId, token } = customerHubPreferences.identity(), version = this.version
      this.healthScope = ""
      // Let the adapter's permission watcher clear its prior snapshot first.
      Promise.resolve().then(() => {
        if (this.current(userId, token, version) && !this.state.busy && this.state.coverage) this.requestHealth(this.state.coverage, userId, token, version)
      })
    })
  }

  stop() { this.unwatch?.(); this.unwatchHealth?.(); this.unwatch = null; this.unwatchHealth = null; this.reset() }

  ensureOwner() {
    const { userId, token } = customerHubPreferences.identity()
    if (!customerHubPreferences.isEnabled() || !userId || !token) { this.reset(); return null }
    if (this.state.owner !== userId || this.token !== token) {
      this.reset()
      this.state.owner = userId
      this.token = token
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey(userId)) || "{}")
        this.state.decisions = storedDecisions(stored)
      } catch (_) { this.state.decisions = {} }
    }
    return { userId, token }
  }

  reset() {
    this.version++
    this.token = ""
    this.pending = null
    this.healthScope = ""
    Object.assign(this.state, { owner: "", busy: false, error: "", decisionError: "", activities: [], checkins: [], coverage: null, decisions: {}, requestKey: "" })
    healthAdapter.reset()
  }

  current(owner, token, version) {
    const identity = customerHubPreferences.identity()
    return customerHubPreferences.isEnabled() && this.state.owner === owner && owner === identity.userId && token === identity.token && version === this.version
  }

  load(params = {}, force = false) {
    const identity = this.ensureOwner()
    if (!identity) return Promise.resolve()
    const { userId, token } = identity
    const requestParams = { ...params }
    const key = JSON.stringify(Object.keys(requestParams).sort().map(key => [key, requestParams[key]]))
    if (!force && this.pending?.key === key && this.pending.userId === userId && this.pending.token === token) return this.pending.promise
    if (!force && this.state.requestKey === key && !this.state.error && this.state.coverage) return Promise.resolve()
    const version = ++this.version
    this.state.busy = true
    this.state.error = ""
    this.state.requestKey = key
    // Never show the previous period's totals while the new period is loading.
    this.state.activities = []
    this.state.checkins = []
    this.state.coverage = null
    this.healthScope = ""
    healthAdapter.resetActivities()
    const request = { key, userId, token, version, promise: null }
    this.pending = request
    request.promise = (async () => {
      try {
        const response = await customerHubApi.getActivity(requestParams, token)
        if (!this.current(userId, token, version)) return
        if (response?.success !== true || String(response.userId) !== userId || !Array.isArray(response.activities) || !Array.isArray(response.checkins) || !response.coverage || typeof response.coverage !== "object" || Array.isArray(response.coverage)) throw new Error("Ungültige Aktivitätsantwort")
        this.state.activities = response.activities
        this.state.checkins = response.checkins
        this.state.coverage = response.coverage
        Promise.resolve().then(() => {
          if (this.current(userId, token, version)) return this.requestHealth(response.coverage, userId, token, version)
        }).catch(() => {})
      } catch (error) {
        if (this.current(userId, token, version)) {
          this.state.error = "Deine Aktivitäten konnten nicht geladen werden. Bitte versuche es erneut."
          if ([401, 403].includes(error?.response?.status)) customerHubPreferences.resolve(userId, token, [])
        }
      }
    })().finally(() => {
      if (this.pending !== request) return
      this.pending = null
      if (this.current(userId, token, version)) this.state.busy = false
      else this.reset()
    })
    return request.promise
  }

  requestHealth(coverage, userId, token, version) {
    if (!this.current(userId, token, version)) return
    // Health may predate the first Roody workout. The adapter independently
    // bounds the readable device history and reports that limit to the user.
    const deviceFrom = hubDay(Date.now() - 29 * 86400000)
    const from = coverage.allHistory ? [coverage.from || deviceFrom, deviceFrom].sort()[0] : coverage.from || hubDay()
    const to = coverage.to || hubDay()
    this.healthScope = healthAdapter.scope()
    try { return Promise.resolve(healthAdapter.loadRange(from, to)).catch(() => {}) }
    catch (_) { this.healthScope = "" }
  }

  entries() {
    if (!customerHubPreferences.isEnabled() || this.state.owner !== customerHubPreferences.identity().userId || this.token !== customerHubPreferences.identity().token) return []
    const scope = healthAdapter.scope()
    return buildHubActivities(this.state.activities, scope && scope === this.healthScope ? healthAdapter.state.workouts : [], this.state.decisions)
  }

  statistics(range) {
    const { userId, token } = customerHubPreferences.identity()
    if (!customerHubPreferences.isEnabled() || this.state.owner !== userId || this.token !== token) return hubStatistics([], [])
    const selectedRange = range || this.state.coverage || {}
    return hubStatistics(this.entries(), this.state.checkins, selectedRange.period === "all" || selectedRange.allHistory ? {} : selectedRange)
  }

  undoMerge(activity) {
    if (!this.ensureOwner()) return
    const current = this.entries().find(item => item.id === activity?.id && item.resolution === "confirmed")
    if (!current) return
    const refs = new Set(current.sourceIds), decisions = { ...this.state.decisions }
    Object.entries(this.state.decisions).forEach(([id, decision]) => {
      if (decision?.action === "merge" && (refs.has(id) || refs.has(decision.targetId))) delete decisions[id]
    })
    this.saveDecisions(decisions)
  }

  decide(id, action, targetId = "") {
    if (!this.ensureOwner()) return
    const item = this.entries().find(row => row.id === id)
    if (action !== "clear" && (!item || !["merge", "separate"].includes(action))) return
    if (action === "merge" && !item.overlapCandidates.some(other => other.id === targetId)) return
    const decisions = { ...this.state.decisions }
    if (action === "clear") delete decisions[id]
    else decisions[id] = action === "merge" ? { action, targetId } : { action, targetIds: item.overlapCandidates.map(other => other.id) }
    this.saveDecisions(decisions)
  }

  saveDecisions(decisions) {
    this.state.decisions = decisions
    this.state.decisionError = ""
    try { localStorage.setItem(storageKey(this.state.owner), JSON.stringify(decisions)) }
    catch (_) { this.state.decisionError = "Die Zuordnung gilt für diese Sitzung. Sie konnte auf diesem Gerät nicht gespeichert werden." }
  }
}

export default new CustomerHubActivityService()
